import { faCircleNotch } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Card, CardActions, CardContent, Stack, Typography } from "@mui/material";
import { MprcCardHeaderColor, MprcRequestErrorReport } from "components";
import { InviteProcessResponse } from "generated/CoreApi_v1";
import { useInsideLayout } from "layouts/common/inside/InsideLayoutContext";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { RootPaths } from "routes/RootRouter";
import { LocationState } from "views/public/LoginPage";

/**
 * Confirmação de Convite ao Canal.
 * @param props
 */
export const FeedChannelInvite: React.FC = () => {
    const { t } = useTranslation("knowledge");
    const layout = useInsideLayout();
    const session = useSession();

    const api = useCoreApi();
    const navigate = useNavigate();
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();

    const [status, setStatus] = useState<"init" | "paramError" | "processing" | "done" | "newLogin">("init");
    const [data, setData] = useState<InviteProcessResponse | null>(null);

    const [viewError, setViewError] = useState<any>();
    const [searchParams] = useSearchParams();

    const userId = searchParams.get("au");
    const action = searchParams.get("ao");
    const inviteId = searchParams.get("ai");
    const notificationId = searchParams.get("an");

    async function doProcessInvite(accepted: boolean) {
        setStatus("processing");

        try {
            const response = await api.v1.channelManagement.inviteProcess({ inviteId: inviteId!, notificationId: notificationId!, accepted });
            if (response.data.success && accepted) {
                enqueueSnackbar(t("Feed.Channel.Invite.AcceptSuccessfull"), { variant: "success" });
                navigate(KnowledgePaths.FeedChannel(response.data.channelId!));
            }
            else {
                setData(response.data);
            }
        }
        catch (e: any) {
            setViewError(e);
        }
        finally {
            setStatus("done");
        }
    }

    const newLogin = () => {
        session.finalizeSession(() => {
            navigate(RootPaths.Login, {
                state: {
                    from: location,
                    reason: t("Feed.Channel.Invite.LogoutMessage")
                } as LocationState
            });
        });
    };

    ///
    useEffect(() => {
        layout.changeSubHeaderText(t("Feed.Channel.Invite.Title"));

        if (userId && inviteId && notificationId && action) {
            if (userId != session.user.userId)
                setStatus("newLogin");
            else
                doProcessInvite(action == "accept");
        }
        else setStatus("paramError");
    }, []);

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////////////////////

    return (
        <Stack>
            <Card>
                <MprcCardHeaderColor color="primary" title={t("Feed.Channel.Invite.Title")} />
                <CardContent sx={{ p: 4 }}>
                    {
                        viewError
                            ? <MprcRequestErrorReport error={viewError} />
                            : status == "paramError"
                                ? <Typography variant="h5" color="error">
                                    {t("Feed.Channel.Invite.ParamsErrorMessage")}
                                </Typography>
                                : status == "newLogin"
                                    ? <Typography variant="h5" color="warning">
                                        {t("Feed.Channel.Invite.LogoutMessage")}
                                    </Typography>
                                    : status == "processing"
                                        ? (
                                            <Stack direction="row" justifyContent="start" alignItems="center" gap={1}>
                                                <div><FontAwesomeIcon icon={faCircleNotch} spin fixedWidth size={"2x"} /></div>
                                                <div>{t("Feed.Channel.Invite.ProcessingMessage")}</div>
                                            </Stack>
                                        )
                                        : status == "done" && data?.success == true
                                            ? <Typography variant="h4" color="success">
                                                {t("Feed.Channel.Invite.RefuseSuccessfull")}
                                            </Typography>
                                            : status == "done" && data?.success == false
                                                ? <Typography variant="h2" color="error">
                                                    {data.errorMessage}
                                                </Typography>
                                                : null
                    }
                </CardContent>
                {(status == "paramError" || status == "done") &&
                    <CardActions sx={{ justifyContent: "center" }}>
                        <Button
                            variant="contained"
                            onClick={() => navigate(RootPaths.Home)}
                        >
                            {t("Feed.Channel.Invite.GoHome")}
                        </Button>
                    </CardActions>
                }
                {status == "newLogin" &&
                    <CardActions sx={{ justifyContent: "center" }}>
                        <Button
                            variant="contained"
                            onClick={newLogin}
                        >
                            {t("core:Public.Common.LoginButton")}
                        </Button>
                    </CardActions>
                }
            </Card>
        </Stack>
    );
};
